import { Component, OnInit } from '@angular/core';
import { ManagementApiService } from '../../../../../services';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UserSettingModel } from '../../../../settings/models/informate';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActiveToast } from 'ngx-toastr';
import { CustomToastService } from '../../../../../services/custom-toast.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ErrorDialogComponent } from 'src/app/shared/info-dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/info-dialogs/success-dialog/success-dialog.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-calculation-settings',
    templateUrl: './calculation-settings.component.html',
    styleUrls: ['./calculation-settings.component.scss'],
})
export class CalculationSettingsComponent implements OnInit {
    destroy$ = new Subject();
    resultOptions: string[] = [
        'Applied Bolt Load Stress Percentage',
        'Bolt Quantity',
        'Bolt Size',
        'Bolt Load',
        'Combined Weight',
        'Extension',
        'Joint Thickness',
        'Manufacturer Allows Clamp Alignment',
        'Minimum Bolt Length',
        'Nut AF',
        'Percentage Residual Yield',
        'Residual Bolt Load',
        'Residual Bolt Stress',
        'Torque Long Form',
        'Torque Short Form',
        'Turn Of Nut',
    ];
    resultOptionsMap;
    resultOptionsChecked: string;
    profileSettingAreaId: string;
    userSettings: UserSettingModel[];
    form: UntypedFormGroup;

    constructor(
        private fb: UntypedFormBuilder,
        private mgmtApi: ManagementApiService,
        private mgmtSvc: ManagementApiService,
        private ActiveModel: NgbActiveModal,
        private customToastService: CustomToastService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private modelService: NgbModal
    ) {}

    ngOnInit(): void {
        this.getSettings();
    }

    buildForm(): void {
        this.form = null;
        this.form = this.fb.group({});

        this.userSettings.forEach((obj, index) => {
            const validatorsToAdd = [];

            if (obj.setting.isRequired) {
                validatorsToAdd.push(Validators.required);
            }

            this.form.addControl(obj.setting.key, this.fb.control(this.getFormStateValue(obj), validatorsToAdd));
        });
    }

    getFormStateValue(userSetting: UserSettingModel) {
        if (userSetting.value) {
            return userSetting.value;
        }

        if (userSetting.settingValue) {
            return userSetting.settingValue.settingValueId;
        }

        return null;
    }

    initialise(): void {
        this.resultOptionsChecked = this.mgmtSvc.userDefaults.find((x) => x.userPreferenceKey == 'CalculationOutputs').userPreference;
        const checkedOptionArray: string[] = this.resultOptionsChecked.split(',');

        this.resultOptionsMap = {};
        for (var x = 0; x < this.resultOptions.length; x++) {
            this.resultOptionsMap[this.resultOptions[x].replace(/\s+/g, '_')] = checkedOptionArray.includes(this.resultOptions[x]);
        }
    }

    setFieldValue(option, e): void {
        this.resultOptionsMap[option.replace(/\s+/g, '_')] = e.checked;

        const filtered = this.resultOptions.filter((item) => {
            return this.resultOptionsMap[item.replace(/\s+/g, '_')] === true;
        });

        this.form.get(this.setFieldKey()).setValue(filtered.join(','));
        if (this.resultOptionsChecked !== filtered.join(',')) {
            this.form.get(this.setFieldKey()).markAsDirty();
            this.form.get(this.setFieldKey()).markAsTouched();
        } else {
            this.form.get(this.setFieldKey()).markAsPristine();
        }

        //console.log(filtered, this.resultOptionsMap)
    }

    setCheckedValue(option) {
        const checkedOptionArray: string[] = this.resultOptionsChecked?.split(',');
        return checkedOptionArray.includes(option);
    }

    setFieldKey(): string {
        return 'CalculationOutputs';
    }

    closeModal() {
        this.ActiveModel.close();
    }

    getSettings(): void {
        this.mgmtApi
            .getSettingProfileAreaId('informate')
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                if (res.success === true) {
                    this.profileSettingAreaId = res.response.settingAreaId;
                    if (this.profileSettingAreaId) {
                        this.ngxUiLoaderService.start();
                        this.mgmtApi
                            .getSettingbySettinfAreaId(this.profileSettingAreaId)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(
                                (userSettings) => {
                                    if (userSettings.success === true) {
                                        this.userSettings = userSettings.response;
                                        this.initialise();
                                        this.buildForm();
                                        this.ngxUiLoaderService.stop();
                                    } else {
                                        this.ngxUiLoaderService.stop();
                                        this.customToastService.errorToast(userSettings.message, 'Error');
                                    }
                                },
                                (err) => {
                                    this.ngxUiLoaderService.stop();
                                    this.customToastService.errorToast(err.message, 'Error');
                                }
                            );
                    }
                } else {
                    this.customToastService.errorToast(res.message, 'Error');
                }
            });
    }

    async Save() {
        const userSetting: UserSettingModel = this.userSettings.find((item) => item.setting.key === 'CalculationOutputs');
        const json = {};
        if (userSetting) {
            json['userSettingId'] = userSetting.userSettingId;
            json['userId'] = userSetting.userId;
            json['settingId'] = userSetting.setting.settingId;
            if (userSetting.value != null || userSetting.value != undefined) {
                const filtered = this.resultOptions.filter((item) => {
                    return this.resultOptionsMap[item.replace(/\s+/g, '_')] === true;
                });
                json['value'] = filtered.join(','); //userSetting.value
            }
        }
        // update mgmt service!
        if (json) {
            await this.updateSettingsInformate(json);
        }
    }

    async updateSettingsInformate(json) {
        const updateJson = [];
        updateJson.push(json);
        let activeToast: ActiveToast<any>;
        await this.mgmtApi.updateUserSettings(updateJson).subscribe((result) => {
            if (result.success === true) {
                // this.customToastService.successToast('User settings updated!', 'Success');
                let dialogRef = this.modelService.open(SuccessDialogComponent);
                dialogRef.componentInstance.message = 'User settings update!';
                this.mgmtApi.refreshUserDefaults();
                this.ActiveModel.close(json['value']);
            } else {
                let dialogRef = this.modelService.open(ErrorDialogComponent);
                dialogRef.componentInstance.message = result.message;
            }
        });
    }

    saveDisabled(): boolean {
        return this.informateFormState();
    }

    informateFormState(): boolean {
        if (!this.form) return true;

        return !(this.form.valid && this.form.dirty);
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
